import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AnimationConfig {
  duration: number;
  easing: string;
  delay?: number;
}

export interface MicroInteraction {
  id: string;
  trigger: 'hover' | 'click' | 'focus' | 'load' | 'change';
  animation: string;
  config: AnimationConfig;
  target?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UIAnimationsService {
  private animationsEnabled = new BehaviorSubject<boolean>(true);
  private reducedMotion = new BehaviorSubject<boolean>(false);
  
  animationsEnabled$ = this.animationsEnabled.asObservable();
  reducedMotion$ = this.reducedMotion.asObservable();

  // Configurações de animação predefinidas
  private animations = {
    // Animações de entrada
    fadeIn: { duration: 300, easing: 'ease-out' },
    slideInUp: { duration: 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    slideInDown: { duration: 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    slideInLeft: { duration: 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    slideInRight: { duration: 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    scaleIn: { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
    bounceIn: { duration: 600, easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)' },
    
    // Animações de saída
    fadeOut: { duration: 200, easing: 'ease-in' },
    slideOutUp: { duration: 300, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)' },
    slideOutDown: { duration: 300, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)' },
    scaleOut: { duration: 200, easing: 'ease-in' },
    
    // Micro-interações
    pulse: { duration: 1000, easing: 'ease-in-out' },
    shake: { duration: 500, easing: 'ease-in-out' },
    bounce: { duration: 600, easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)' },
    wiggle: { duration: 400, easing: 'ease-in-out' },
    glow: { duration: 800, easing: 'ease-in-out' },
    
    // Transições de estado
    expand: { duration: 300, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    collapse: { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)' },
    morphButton: { duration: 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
    
    // Animações de carregamento
    spin: { duration: 1000, easing: 'linear' },
    loadingPulse: { duration: 1500, easing: 'ease-in-out' },
    shimmer: { duration: 2000, easing: 'ease-in-out' },
  };

  constructor() {
    this.detectMotionPreferences();
    this.setupAnimationStyles();
  }

  // Controle de Animação
  enableAnimations(enabled: boolean = true) {
    this.animationsEnabled.next(enabled);
    this.updateAnimationState();
  }

  isAnimationEnabled(): boolean {
    return this.animationsEnabled.value && !this.reducedMotion.value;
  }

  // Métodos de Animação de Elementos
  animateElement(
    element: HTMLElement, 
    animationName: keyof typeof this.animations,
    options?: Partial<AnimationConfig>
  ): Promise<void> {
    if (!this.isAnimationEnabled()) {
      return Promise.resolve();
    }

    const config = { ...this.animations[animationName], ...options };
    
    return new Promise((resolve) => {
      element.style.animation = `${animationName} ${config.duration}ms ${config.easing}`;
      
      if (config.delay) {
        element.style.animationDelay = `${config.delay}ms`;
      }

      const handleAnimationEnd = () => {
        element.removeEventListener('animationend', handleAnimationEnd);
        element.style.animation = '';
        element.style.animationDelay = '';
        resolve();
      };

      element.addEventListener('animationend', handleAnimationEnd);
    });
  }

  // Auxiliares de Micro-interação
  addHoverEffect(element: HTMLElement, effect: 'lift' | 'glow' | 'scale' | 'tilt' = 'lift') {
    if (!this.isAnimationEnabled()) return;

    element.classList.add(`hover-${effect}`);
    
    // Adiciona propriedades CSS customizadas para efeitos dinâmicos
    element.style.setProperty('--hover-duration', '200ms');
    element.style.setProperty('--hover-easing', 'cubic-bezier(0.25, 0.46, 0.45, 0.94)');
  }

  addClickEffect(element: HTMLElement, effect: 'ripple' | 'bounce' | 'pulse' = 'ripple') {
    if (!this.isAnimationEnabled()) return;

    const handleClick = (event: MouseEvent) => {
      switch (effect) {
        case 'ripple':
          this.createRippleEffect(element, event);
          break;
        case 'bounce':
          this.animateElement(element, 'bounce');
          break;
        case 'pulse':
          this.animateElement(element, 'pulse');
          break;
      }
    };

    element.addEventListener('click', handleClick);
    
    // Retorna função de limpeza
    return () => {
      element.removeEventListener('click', handleClick);
    };
  }

  addFocusEffect(element: HTMLElement) {
    if (!this.isAnimationEnabled()) return;

    const handleFocus = () => {
      element.classList.add('focus-enhanced');
    };

    const handleBlur = () => {
      element.classList.remove('focus-enhanced');
    };

    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);

    return () => {
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
    };
  }

  // Animações de Carregamento
  showLoadingAnimation(element: HTMLElement, type: 'spinner' | 'pulse' | 'shimmer' = 'spinner') {
    if (!this.isAnimationEnabled()) {
      element.classList.add('loading-static');
      return;
    }

    element.classList.add(`loading-${type}`);
    element.setAttribute('aria-busy', 'true');
  }

  hideLoadingAnimation(element: HTMLElement) {
    element.classList.remove('loading-spinner', 'loading-pulse', 'loading-shimmer', 'loading-static');
    element.removeAttribute('aria-busy');
  }

  // Animações de Transição de Estado
  animateStateChange(
    element: HTMLElement,
    fromState: string,
    toState: string,
    duration: number = 300
  ): Promise<void> {
    if (!this.isAnimationEnabled()) {
      element.classList.remove(fromState);
      element.classList.add(toState);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      element.style.transition = `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      
      element.classList.remove(fromState);
      element.classList.add(toState);

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  }

  // Animações do Painel de Propriedades
  animatePropertyGroupExpand(element: HTMLElement): Promise<void> {
    if (!this.isAnimationEnabled()) return Promise.resolve();

    const content = element.querySelector('.property-group-content') as HTMLElement;
    if (!content) return Promise.resolve();

    const height = content.scrollHeight;
    
    return new Promise((resolve) => {
      content.style.height = '0px';
      content.style.overflow = 'hidden';
      content.style.transition = 'height 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';

      requestAnimationFrame(() => {
        content.style.height = `${height}px`;
        
        setTimeout(() => {
          content.style.height = '';
          content.style.overflow = '';
          content.style.transition = '';
          resolve();
        }, 300);
      });
    });
  }

  animatePropertyGroupCollapse(element: HTMLElement): Promise<void> {
    if (!this.isAnimationEnabled()) return Promise.resolve();

    const content = element.querySelector('.property-group-content') as HTMLElement;
    if (!content) return Promise.resolve();

    return new Promise((resolve) => {
      const height = content.scrollHeight;
      content.style.height = `${height}px`;
      content.style.overflow = 'hidden';
      content.style.transition = 'height 250ms cubic-bezier(0.55, 0.055, 0.675, 0.19)';

      requestAnimationFrame(() => {
        content.style.height = '0px';
        
        setTimeout(() => {
          content.style.height = '';
          content.style.overflow = '';
          content.style.transition = '';
          resolve();
        }, 250);
      });
    });
  }

  // Animações do Inspetor
  animateInspectorSlideIn(element: HTMLElement, direction: 'left' | 'right' | 'up' | 'down' = 'right'): Promise<void> {
    if (!this.isAnimationEnabled()) return Promise.resolve();

    const transforms = {
      left: 'translateX(-100%)',
      right: 'translateX(100%)',
      up: 'translateY(-100%)',
      down: 'translateY(100%)',
    };

    element.style.transform = transforms[direction];
    element.style.opacity = '0';
    element.style.transition = 'transform 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 300ms ease-out';

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        element.style.transform = 'translate(0, 0)';
        element.style.opacity = '1';
        
        setTimeout(() => {
          element.style.transform = '';
          element.style.opacity = '';
          element.style.transition = '';
          resolve();
        }, 400);
      });
    });
  }

  // Animações de Notificação
  animateNotification(element: HTMLElement, type: 'success' | 'error' | 'warning' | 'info' = 'info'): Promise<void> {
    if (!this.isAnimationEnabled()) return Promise.resolve();

    const animations = {
      success: 'bounceIn',
      error: 'shake',
      warning: 'pulse',
      info: 'slideInDown',
    };

    return this.animateElement(element, animations[type] as keyof typeof this.animations);
  }

  // Animações de Arrastar e Soltar
  animateDragStart(element: HTMLElement) {
    if (!this.isAnimationEnabled()) return;

    element.style.transform = 'scale(1.05) rotate(2deg)';
    element.style.transition = 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)';
    element.style.zIndex = '1000';
    element.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
  }

  animateDragEnd(element: HTMLElement) {
    if (!this.isAnimationEnabled()) {
      this.resetDragStyles(element);
      return;
    }

    element.style.transform = 'scale(1) rotate(0deg)';
    element.style.transition = 'transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    
    setTimeout(() => {
      this.resetDragStyles(element);
    }, 300);
  }

  // Métodos Privados
  private createRippleEffect(element: HTMLElement, event: MouseEvent) {
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = document.createElement('div');
    ripple.className = 'ripple-effect';
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    element.appendChild(ripple);

    setTimeout(() => {
      if (ripple.parentNode) {
        ripple.parentNode.removeChild(ripple);
      }
    }, 600);
  }

  private resetDragStyles(element: HTMLElement) {
    element.style.transform = '';
    element.style.transition = '';
    element.style.zIndex = '';
    element.style.boxShadow = '';
  }

  private detectMotionPreferences() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion.next(mediaQuery.matches);
    
    mediaQuery.addEventListener('change', (e) => {
      this.reducedMotion.next(e.matches);
      this.updateAnimationState();
    });
  }

  private updateAnimationState() {
    const enabled = this.isAnimationEnabled();
    document.documentElement.classList.toggle('animations-disabled', !enabled);
    document.documentElement.classList.toggle('animations-enabled', enabled);
  }

  private setupAnimationStyles() {
    const style = document.createElement('style');
    style.textContent = this.getAnimationCSS();
    document.head.appendChild(style);
  }

  private getAnimationCSS(): string {
    return `
      /* Animation keyframes */
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes slideInUp {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes slideInDown {
        from { transform: translateY(-100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes scaleIn {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      
      @keyframes bounceIn {
        0% { transform: scale(0.3); opacity: 0; }
        50% { transform: scale(1.05); }
        70% { transform: scale(0.9); }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
        20%, 40%, 60%, 80% { transform: translateX(10px); }
      }
      
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      /* Hover effects */
      .hover-lift {
        transition: transform var(--hover-duration, 200ms) var(--hover-easing, ease);
      }
      .hover-lift:hover {
        transform: translateY(-2px);
      }
      
      .hover-glow {
        transition: box-shadow var(--hover-duration, 200ms) var(--hover-easing, ease);
      }
      .hover-glow:hover {
        box-shadow: 0 0 20px rgba(24, 144, 255, 0.3);
      }
      
      .hover-scale {
        transition: transform var(--hover-duration, 200ms) var(--hover-easing, ease);
      }
      .hover-scale:hover {
        transform: scale(1.02);
      }
      
      /* Focus effects */
      .focus-enhanced {
        outline: 2px solid #1890ff;
        outline-offset: 2px;
        box-shadow: 0 0 0 4px rgba(24, 144, 255, 0.1);
      }
      
      /* Loading animations */
      .loading-spinner::after {
        content: '';
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #1890ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      .loading-pulse {
        animation: pulse 1.5s ease-in-out infinite;
      }
      
      .loading-shimmer {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 2s ease-in-out infinite;
      }
      
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      
      /* Ripple effect */
      .ripple-effect {
        position: absolute;
        border-radius: 50%;
        background: rgba(24, 144, 255, 0.3);
        transform: scale(0);
        animation: ripple 600ms ease-out;
        pointer-events: none;
      }
      
      @keyframes ripple {
        to {
          transform: scale(2);
          opacity: 0;
        }
      }
      
      /* Reduced motion overrides */
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
      
      .animations-disabled * {
        animation: none !important;
        transition: none !important;
      }
    `;
  }
}
